import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { leadsService, Lead } from '../../services/leads.service'
import { getErrorMessage } from '../../types'
import toast from 'react-hot-toast'

interface GenerateMessagesModalProps {
  leads: Lead[]
  onClose: () => void
}

export default function GenerateMessagesModal({ leads, onClose }: GenerateMessagesModalProps) {
  const queryClient = useQueryClient()
  const [userOffering, setUserOffering] = useState('')
  const [tone, setTone] = useState('friendly_professional')
  const [language, setLanguage] = useState('ru')

  const generateMutation = useMutation({
    mutationFn: () =>
      leadsService.generateMessages(
        leads.map((l) => l.id),
        userOffering,
        tone,
        language
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leadStats'] })
      toast.success(data.message)
      onClose()
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const tones = [
    { value: 'friendly_professional', label: 'Дружелюбный профессиональный' },
    { value: 'casual', label: 'Неформальный' },
    { value: 'formal', label: 'Формальный' },
    { value: 'enthusiastic', label: 'Энтузиастичный' },
    { value: 'consultative', label: 'Консультационный' },
  ]

  const languages = [
    { value: 'ru', label: 'Русский' },
    { value: 'en', label: 'English' },
    { value: 'uk', label: 'Українська' },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!userOffering.trim()) {
      toast.error('Укажите ваше предложение')
      return
    }
    generateMutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-auto overflow-hidden">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Генерация сообщений
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Будет сгенерировано {leads.length} сообщений для одобренных лидов
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* User Offering */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ваше предложение *
                </label>
                <textarea
                  value={userOffering}
                  onChange={(e) => setUserOffering(e.target.value)}
                  rows={4}
                  placeholder="Опишите ваш продукт или услугу, которую вы предлагаете..."
                  className="input w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Это описание будет использовано для генерации персонализированных сообщений
                </p>
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тон сообщения
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="input w-full"
                >
                  {tones.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Язык сообщения
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="input w-full"
                >
                  {languages.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview Leads */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Выбранные лиды ({leads.length})
                </label>
                <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {leads.slice(0, 5).map((lead) => (
                    <div key={lead.id} className="text-sm text-gray-600">
                      {lead.username ? `@${lead.username}` : `ID: ${lead.telegram_user_id}`}
                    </div>
                  ))}
                  {leads.length > 5 && (
                    <div className="text-sm text-gray-500 mt-1">
                      ...и ещё {leads.length - 5}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={generateMutation.isPending}
                className="btn-primary"
              >
                {generateMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Генерация...
                  </span>
                ) : (
                  'Сгенерировать'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
